// packages/ui/authorityLink.mjs — RUN CT. THE LINK BUILDER, WHERE TWO APPS CAN REACH IT.
//
// 🔴 IT MOVED HERE BECAUSE THE MODAL DID, AND A COPY WAS THE ONE THING THAT COULD NOT HAPPEN.
// `AuthorityModal.jsx` was promoted from `apps/main/client/src/screens/Clark/answer/` into this
// package so Clark and Research render the SAME component rather than two lookalikes. The modal
// draws an `Open in Research` link, so the link builder had to come with it — and steel could not
// simply be handed its own copy, because a second implementation of "authority → URL" is precisely
// the defect `clarkAuthorityParity.test.js` was written to prevent: `Jones v. State` is four
// different cases, and the way one becomes a WRONG-CASE link is somebody templating a path from a
// caption in a second place.
//
// ⭐ CLARK'S `answer/authorityLink.mjs` STILL EXISTS AND STILL EXPORTS THIS NAME — it re-exports
// from here. Its other two functions (`storedAnswerHref`, `hasOpenableAuthorities`) did NOT move:
// they are about Clark's stored-answer route and Clark's zero-state gate, nine call sites between
// them, and neither is anything Research asks. Only the shared thing is shared.
//
// It is a `.mjs` for the reason it always was: `node --test` has no JSX loader, so logic that
// decides where a lawyer lands must live where a test can actually import and execute it.

/**
 * 🔴 SAME-ORIGIN, AND THAT IS A FINDING RATHER THAN A CHOICE.
 *
 * The step was written expecting a cross-origin hop from `citeclerk-citations-app` to
 * `citeclerk-research-app`, and expecting to have to build the route. Neither is true. Verified
 * live against the deployed stack:
 *
 *   · `citeclerk.com` is served by the Fly app `citeclerk-router` running Caddy
 *     (`ops/router/Caddyfile`), which proxies `/research/*` to the research app and EVERYTHING
 *     ELSE to the citations app. Clark and Research are therefore the same origin.
 *   · Research's route table already has `/case/:id` (`apps/steel/client/src/App.jsx:44`) under a
 *     `/research` basename (`apps/steel/vite.config.js:39`).
 *   · `GET https://citeclerk.com/research/case/1035893` → **200**. The direct
 *     `citeclerk-research-app.fly.dev/case/1035893` → 404, because the basename lives at the
 *     router. So the canonical link is the relative one, and hard-coding an origin would have
 *     produced the URL that 404s.
 *
 * Which means: no CORS, no env var, no new route, no origin constant that can rot. A relative
 * path. The runbook allowed for building this route; it did not need building.
 */
export function researchHref(authority) {
  const id = authority?.caseId ?? authority?.clusterId ?? null;
  if (id != null && id !== "") return `/research/case/${encodeURIComponent(String(id))}`;

  // ═══ 🔴 RUN 9 · PART 6 — A STATUTE ROW HAD NO DESTINATION, AND `null` WAS WHY ════════════════
  //
  // `caseId` is null for every statute, by construction and correctly: a statute is not a cluster,
  // and inventing a cluster id for one is how `42 U.S.C. § 1983` got resolved to *In Re Korean Air
  // Lines Disaster of September 1, 1983* in the first place. So this function returned null, every
  // caller rendered a row with no link, and the reader could see a statute named as authority with
  // no way to read it.
  //
  // RUN 8 built the door: `/statutes?q=<cite>` takes the FEDERAL branch and renders the full
  // operative text (§ 2612 → 11,760 chars; § 362 → 40,170). Nothing is retrieved to make it work —
  // the citation is the query.
  //
  // 🔴 `/research/statutes?q=…`, NOT `/statutes?q=…`. Steel's router is mounted at
  // `basename="/research"` (main.jsx:42), so the bare path resolves to `citeclerk.com/statutes` —
  // a route that does not exist. From INSIDE Research the fix is the router's `<Link>`, which
  // applies the basename (StatuteLane.jsx does exactly that). From Clark it must be written out,
  // because Clark is a DIFFERENT SPA and steel's router is not in the tree here; the `/research`
  // prefix is Caddy's, and it is the same-origin relative path documented above.
  //
  // The CITE is the query, deliberately, and not the heading: `q=Civil action for deprivation of
  // rights` is a search, and `q=42 U.S.C. § 1983` is an identifier.
  const cite = authority?.cite ?? null;
  if (authority?.kind === "statute" && cite) {
    return `/research/statutes?q=${encodeURIComponent(String(cite))}`;
  }
  return null;
}
